/**
 * XLSX/CSV export for the V2 stats tables, modeled on
 * gpthelp-excel-builder.ts (typed columns, aoa_to_sheet, formula-injection
 * protection). Headers are the on-screen i18n labels; exports always include
 * every column the caller passes.
 */
import XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { sanitizeFilenameBase } from '@/domain/files/filename';

export type TStatsExportColumnType = 'string' | 'number' | 'date';

export interface IStatsExportColumn {
  key: string;
  /** Already-translated header label. */
  header: string;
  type?: TStatsExportColumnType;
}

export interface IStatsExportInput {
  /** Base filename without extension. */
  filename: string;
  sheetName: string;
  columns: IStatsExportColumn[];
  rows: Array<Record<string, unknown>>;
}

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const INVALID_SHEET_CHARS_RE = /[:\\/?*[\]]/g;
const FORMULA_PREFIX_RE = /^[=+\-@]/;

function sanitizeSheetName(input: string): string {
  const cleaned = (input ?? '').replace(INVALID_SHEET_CHARS_RE, ' ').trim();
  const truncated = cleaned.length > 31 ? cleaned.slice(0, 31).trim() : cleaned;
  return truncated || 'Data';
}

/**
 * Normalizes one cell: numbers stay numeric, dates become ISO day strings,
 * everything else is stringified with formula-injection protection
 * (leading = + - @ prefixed with a quote, Excel/Sheets convention).
 */
function sanitizeCellValue(
  value: unknown,
  type?: TStatsExportColumnType
): string | number | null {
  if (value === null || typeof value === 'undefined') return null;

  if (type === 'number') {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : null;
  }

  if (type === 'date') {
    if (value instanceof Date && !isNaN(value.getTime())) {
      // Local getters, NOT toISOString(): these are local-midnight Dates, and
      // UTC conversion would shift them a day back for UTC+ timezones.
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${value.getFullYear()}-${month}-${day}`;
    }
    return String(value);
  }

  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'boolean') return value ? 'true' : 'false';

  const s = String(value);
  return FORMULA_PREFIX_RE.test(s.trimStart()) ? `'${s}` : s;
}

function buildAoa(input: IStatsExportInput): Array<Array<string | number | null>> {
  const headers = input.columns.map((col) => col.header);
  const aoa: Array<Array<string | number | null>> = [headers];
  for (const row of input.rows) {
    aoa.push(
      input.columns.map((col) => sanitizeCellValue(row[col.key], col.type))
    );
  }
  return aoa;
}

export function exportStatsXlsx(input: IStatsExportInput): void {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(buildAoa(input));
  XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(input.sheetName));

  // xlsx@0.16 types write() loosely, but type:'array' returns an ArrayBuffer.
  const bytes: ArrayBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([bytes], { type: XLSX_MIME });
  saveAs(blob, `${sanitizeFilenameBase(input.filename)}.xlsx`);
}

/** RFC 4180 quoting; UTF-8 BOM so Excel detects the encoding. */
function toCsvField(value: string | number | null): string {
  if (value === null) return '';
  const s = String(value);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function exportStatsCsv(input: IStatsExportInput): void {
  const lines = buildAoa(input).map((row) =>
    row.map(toCsvField).join(',')
  );
  const blob = new Blob(['\uFEFF' + lines.join('\r\n')], {
    type: 'text/csv;charset=utf-8',
  });
  saveAs(blob, `${sanitizeFilenameBase(input.filename)}.csv`);
}
